/**
 * MicroWaveform + computePeaks — iter-248
 *
 * Tiny SVG bar-chart of an `AudioBuffer`'s peak-amplitude envelope.
 * Used in the iter-247 Take History modal so users can spot the
 * difference between visually-identical "Take 3" / "Take 4" labels
 * at a glance.
 *
 * Why peaks instead of WaveSurfer / a full canvas waveform?
 *   - 24-32 vertical bars is enough resolution to differentiate
 *     "punchy first beat" from "long sustained tone" from "silence".
 *   - SVG is cheap to mount/unmount inside framer-motion modals.
 *   - No external dep — keeps the iter-246 modal self-contained.
 */
import { memo } from "react";

/**
 * Downsample an AudioBuffer to `n` peak-amplitude buckets (0..1).
 * Reads channel 0; returns a plain Array (not Float32Array) so React
 * can shallow-compare it in props.
 */
export function computePeaks(buffer, n = 32) {
  if (!buffer || typeof buffer.getChannelData !== "function" || !buffer.length) {
    return [];
  }
  const data = buffer.getChannelData(0);
  const bucketSize = Math.max(1, Math.floor(data.length / n));
  const out = new Array(n);
  for (let b = 0; b < n; b++) {
    const start = b * bucketSize;
    const end = Math.min(start + bucketSize, data.length);
    let peak = 0;
    for (let i = start; i < end; i++) {
      const v = data[i] < 0 ? -data[i] : data[i];
      if (v > peak) peak = v;
    }
    out[b] = peak;
  }
  return out;
}

/**
 * Render a row of vertical peak bars. Bars centre on the horizontal
 * midline (so the wave looks symmetric like a stem plot) with a
 * minimum visible height of 1 px so silent buckets stay visible.
 */
function MicroWaveformImpl({
  peaks = [],
  width = 80,
  height = 16,
  color = "currentColor",
  className = "",
  title,
}) {
  if (!Array.isArray(peaks) || peaks.length === 0) {
    return (
      <svg
        width={width}
        height={height}
        className={className}
        aria-hidden="true"
      >
        <line
          x1="0"
          x2={width}
          y1={height / 2}
          y2={height / 2}
          stroke={color}
          strokeOpacity="0.25"
        />
      </svg>
    );
  }
  const n = peaks.length;
  const barW = Math.max(1, Math.floor(width / n));
  const gap = Math.max(0, (width - barW * n) / 2);
  const mid = height / 2;
  return (
    <svg
      width={width}
      height={height}
      className={className}
      role="img"
      aria-label={title || `${n}-bar waveform preview`}
    >
      <g transform={`translate(${gap}, 0)`}>
        {peaks.map((p, i) => {
          const h = Math.max(1, p * (height - 2));
          return (
            <rect
              key={i}
              x={i * barW}
              y={mid - h / 2}
              width={barW > 1 ? barW - 1 : barW}
              height={h}
              rx={barW > 2 ? 0.5 : 0}
              fill={color}
              opacity={0.85}
            />
          );
        })}
      </g>
    </svg>
  );
}

// Memoised — peaks arrays are stable refs (computed once per take via
// useMemo), so a shallow compare cuts re-renders to zero when the
// surrounding modal re-renders for unrelated reasons.
const MicroWaveform = memo(MicroWaveformImpl);
export default MicroWaveform;
